import {
  formatJourneyMinutes,
  type EndpointJourney,
  type RoutedParticipant,
  type TravelMode,
} from './transit/modes.ts'

/** Next actions at known stations, without synthesizing access or dock geometry. */
export function routeTransitions(
  journey: EndpointJourney,
  participant: RoutedParticipant,
): Array<{ lat: number; lon: number; mode: TravelMode | 'transfer'; label: string }> {
  if (journey.kind === 'bike-share') {
    return [
      {
        lat: journey.pickupStation.lat, lon: journey.pickupStation.lon, mode: 'bike-share',
        label: `Pick up a bike at ${journey.pickupStation.name} and ride to ${journey.returnStation.name} (${formatJourneyMinutes(journey.rideSeconds)})`,
      },
      {
        lat: journey.returnStation.lat, lon: journey.returnStation.lon, mode: 'walk',
        label: `Return bike at ${journey.returnStation.name} and walk to destination (${formatJourneyMinutes(journey.egressSeconds)})`,
      },
    ]
  }
  if (journey.kind !== 'scheduled' || !journey.journey.legs.length) return []
  const person = participant.alternatives.find((alternative) => 'result' in alternative && (
    alternative.arriveByJourneys?.some((candidate) => candidate === journey.journey)
    || alternative.result.journeys.some((candidate) => candidate === journey.journey)
  ))
  if (!person || !('result' in person)) return []
  const transitions: ReturnType<typeof routeTransitions> = []
  const add = (index: number, mode: TravelMode | 'transfer', label: string) => {
    const station = person.endpoints[index]
    if (station) transitions.push({ lat: station[2], lon: station[3], mode, label })
  }
  for (const [index, leg] of journey.journey.legs.entries()) {
    if (leg.kind === 'ride') {
      const route = person.routes[leg.routeIndex]
      const station = person.endpoints[leg.fromStation]
      const mode = route?.[3] ?? station?.[4]
        ?? (person.allowedModes.length === 1 ? person.allowedModes[0] : undefined)
      if (!route || !station || !mode) continue
      const previous = journey.journey.legs[index - 1]
      const change = previous?.kind === 'ride' && previous.toStation === leg.fromStation
      const service = `${route[1]} ${mode === 'subway' ? 'train' : mode}`
      add(leg.fromStation, mode, `${change ? 'Change to' : 'Board'} ${service} at ${station[1]}`)
    } else if (leg.kind === 'transfer') {
      const target = person.endpoints[leg.toStation]?.[1] ?? 'next station'
      const walking = leg.walkingSeconds !== undefined
      add(leg.fromStation, walking ? 'walk' : 'transfer',
        `${walking ? 'Walk' : 'Transfer'} to ${target} (${formatJourneyMinutes(leg.walkingSeconds ?? leg.seconds)})`)
    }
  }
  const egress = journey.bikeShareEgress
  const station = person.endpoints[journey.endpointIndex]
  if (station) {
    const action = journey.journey.legs.at(-1)?.kind === 'ride'
      ? `Alight at ${station[1]} and walk` : `Walk from ${station[1]}`
    add(journey.endpointIndex, 'walk',
      `${action} to ${egress?.pickupName ?? 'destination'} (${formatJourneyMinutes(egress?.pickupWalkSeconds ?? journey.egressSeconds)})`)
  }
  return transitions
}
